/** Shared component primitives for the gate panels. Pure React. */
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, XCircle, Gauge } from 'lucide-react';
import { asTextG, objG, DECISION_CLS, SEVERITY_CLS } from './gateConstants';
import { ConfidenceBadge } from './ConfidenceBadge';

export function QualityChecks({ checks }: { checks: Record<string, unknown> }) {
  const entries = Object.entries(checks);
  if (entries.length === 0) return null;
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        Quality checks ({entries.length})
      </p>
      <ul className="space-y-1">
        {entries.map(([name, raw]) => {
          const c = objG(raw);
          const passed = c.passed === true;
          const conf = c.confidence_score;
          const finding = asTextG(c.finding);
          return (
            <li key={name} className="flex items-start gap-2 rounded border px-2 py-1.5 text-xs">
              {passed ? (
                <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 shrink-0 mt-0.5" aria-hidden="true" />
              ) : (
                <XCircle className="h-4 w-4 text-red-600 dark:text-red-400 shrink-0 mt-0.5" aria-hidden="true" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium capitalize">{name.replace(/_/g, ' ')}</span>
                  {typeof conf === 'number' && (
                    <span className="text-[10px] text-muted-foreground">({conf}/100)</span>
                  )}
                </div>
                {finding && <p className="text-muted-foreground mt-0.5">{finding}</p>}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function BulletList({
  items,
  severity,
  label,
}: {
  items: unknown[];
  severity: 'block' | 'warn';
  label: string;
}) {
  if (items.length === 0) return null;
  const tone = severity === 'block'
    ? 'border-red-200 bg-red-50 text-red-900 dark:border-red-700 dark:bg-red-950/30 dark:text-red-200'
    : 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-200';
  return (
    <div className={`rounded border p-2 text-xs ${tone}`}>
      <p className="font-medium mb-1">{label} ({items.length})</p>
      <ul className="list-disc list-inside space-y-0.5">
        {items.map((it, i) => (
          <li key={i}>
            {typeof it === 'string'
              ? it
              : asTextG(objG(it).description ?? objG(it).message)}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function GateHeaderCard({
  decision,
  rationale,
  confidence,
}: {
  decision: string;
  rationale: string;
  confidence: unknown;
}) {
  const cls = DECISION_CLS[decision] ?? '';
  return (
    <Card className="border-border">
      <CardContent className="pt-3 pb-3 px-4 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Gauge className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <span className="text-xs uppercase tracking-wide text-muted-foreground">Decision</span>
          <Badge variant="outline" className={`text-xs ${cls}`}>{decision}</Badge>
          <span className="ml-auto">
            <ConfidenceBadge score={confidence} rationale={rationale} />
          </span>
        </div>
        {rationale && <p className="text-xs text-muted-foreground">{rationale}</p>}
      </CardContent>
    </Card>
  );
}

export function FindingRow({ row }: { row: Record<string, unknown> }) {
  const sev = asTextG(row.severity) || 'MEDIUM';
  const cls = SEVERITY_CLS[sev] ?? '';
  return (
    <li className="rounded border px-2 py-1.5 text-xs space-y-0.5">
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant="outline" className={`text-[10px] shrink-0 ${cls}`}>{sev}</Badge>
        {asTextG(row.category) && (
          <span className="text-muted-foreground">{asTextG(row.category)}</span>
        )}
        {asTextG(row.source_agent) && (
          <span className="text-muted-foreground">· {asTextG(row.source_agent)}</span>
        )}
        {asTextG(row.priority) && (
          <Badge variant="outline" className="text-[10px]">{asTextG(row.priority)}</Badge>
        )}
        {(asTextG(row.finding_id) || asTextG(row.recommendation_id)) && (
          <span className="text-[10px] text-muted-foreground ml-auto">
            {asTextG(row.finding_id) || asTextG(row.recommendation_id)}
          </span>
        )}
      </div>
      {asTextG(row.finding) && <p>{asTextG(row.finding)}</p>}
      {asTextG(row.action) && <p className="font-medium">{asTextG(row.action)}</p>}
      {asTextG(row.rationale) && (
        <p className="text-muted-foreground italic">→ {asTextG(row.rationale)}</p>
      )}
    </li>
  );
}
